import { useCallback, useEffect, useRef, useState } from "react"
import { useUptimeViewRunProbeMutation } from "@/v2-backoffice-client/uptime/view/runProbe/uptime.view.runProbe.mutation"

// How often to probe a target while its detail sheet is open. Short
// enough to feel real-time, long enough to not overload slow targets.
const DEFAULT_INTERVAL_MS = 5_000

// Automatic probe session cap. We stop pushing new probes after this
// so a forgotten tab doesn't rack up probes indefinitely; the user
// can manually re-arm by closing and reopening the sheet (or, later,
// via an explicit "Keep probing" button).
const DEFAULT_MAX_DURATION_MS = 15 * 60 * 1000

export type UptimeLiveProbeOptions = {
  // When false, the hook tears down any in-flight timer. Passing
  // `open` makes the pattern `useUptimeLiveProbe({ open: isOpen })`
  // at the call site.
  enabled: boolean
  source: string
  identifier: string
  intervalMs?: number
  maxDurationMs?: number
  // Called after each probe so the caller can refetch dependent
  // queries (chart data, incidents, etc).
  onProbe?(): void
}

export type UptimeLiveProbeState = {
  // Wall clock timestamp (ms) of the last successful probe; null
  // before the first one fires.
  lastProbeAt: number | null
  // Scheduled ms timestamp for the next probe; null when paused or
  // stopped.
  nextProbeAt: number | null
  isProbing: boolean
  isStopped: boolean
  stoppedReason: "cap" | null
}

// useUptimeLiveProbe drives the "live timeline" behaviour of the
// detail sheet: while open + visible, it calls runUptimeProbe on a
// fixed interval, auto-pauses when the tab is backgrounded, and
// auto-stops after a duration cap so an idle tab doesn't probe
// forever.
//
// Returns the probe state so callers can render a "next probe in Xs"
// indicator.
export function useUptimeLiveProbe(
  opts: UptimeLiveProbeOptions,
): UptimeLiveProbeState {
  const {
    enabled,
    source,
    identifier,
    intervalMs = DEFAULT_INTERVAL_MS,
    maxDurationMs = DEFAULT_MAX_DURATION_MS,
    onProbe,
  } = opts

  const [runProbe, { loading }] = useUptimeViewRunProbeMutation()

  const [state, setState] = useState<UptimeLiveProbeState>({
    lastProbeAt: null,
    nextProbeAt: null,
    isProbing: false,
    isStopped: false,
    stoppedReason: null,
  })

  // Stable refs so the effect body can close over the latest values
  // without re-running (which would reset the timer on every render).
  const onProbeRef = useRef(onProbe)
  onProbeRef.current = onProbe
  const runProbeRef = useRef(runProbe)
  runProbeRef.current = runProbe

  const isProbing = loading
  useEffect(() => {
    setState((s) => ({ ...s, isProbing }))
  }, [isProbing])

  const probe = useCallback(async () => {
    try {
      await runProbeRef.current({
        variables: { input: { source, identifier } },
      })
      onProbeRef.current?.()
      setState((s) => ({ ...s, lastProbeAt: Date.now() }))
    } catch {
      // Errors are surfaced through the Apollo error link already;
      // the loop continues and will retry on the next tick rather
      // than tearing down the live session for a single failure.
    }
  }, [source, identifier])

  useEffect(() => {
    if (!enabled) {
      setState({
        lastProbeAt: null,
        nextProbeAt: null,
        isProbing: false,
        isStopped: false,
        stoppedReason: null,
      })
      return
    }

    const sessionStart = Date.now()
    let timerId: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const schedule = (delay: number) => {
      if (cancelled) return
      const nextAt = Date.now() + delay
      setState((s) => ({ ...s, nextProbeAt: nextAt }))
      timerId = setTimeout(async () => {
        if (cancelled) return
        if (Date.now() - sessionStart >= maxDurationMs) {
          setState((s) => ({
            ...s,
            nextProbeAt: null,
            isStopped: true,
            stoppedReason: "cap",
          }))
          return
        }
        if (document.visibilityState === "hidden") {
          // Don't probe for a tab the user isn't looking at; resume
          // as soon as it's focused again via the visibilitychange
          // listener below.
          schedule(intervalMs)
          return
        }
        await probe()
        schedule(intervalMs)
      }, delay)
    }

    // Kick off immediately so the chart updates within 5s of the
    // sheet opening rather than 10s (interval) after.
    schedule(0)

    const onVis = () => {
      if (cancelled) return
      if (document.visibilityState === "visible") {
        // Re-arm: fire a probe as soon as the tab becomes visible
        // so the user doesn't stare at a stale chart.
        if (timerId != null) clearTimeout(timerId)
        schedule(0)
      }
    }
    document.addEventListener("visibilitychange", onVis)

    return () => {
      cancelled = true
      document.removeEventListener("visibilitychange", onVis)
      if (timerId != null) clearTimeout(timerId)
    }
  }, [enabled, probe, intervalMs, maxDurationMs])

  return state
}
